import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { QueryClient } from '@tanstack/angular-query-experimental';
import {
  NotFoundErrorDetailCodeEnum,
  ResponseError,
  SalesApi,
  SalesRepDto,
} from '../connectors/backend';
import { waitForMeAndCompany } from '../guards/company.guard';
import { apiOnline } from '../utils/api-connectivity';
import { ApiErrorDetail, parseApiErrorDetail } from '../utils/api-error';
import {
  AgentUnavailableParams,
  AgentUnavailableReason,
  agentRouteCommands,
  SALES_MODULE_KEY,
} from './agent-route-paths';
import { myRepQueryOptions } from './data/agent-session';

/** Null for a failure that says nothing about the rep's access, such as a 5xx or no network. */
export function repErrorRefusal(
  error: unknown,
  detail: ApiErrorDetail | null,
): AgentUnavailableReason | null {
  if (!(error instanceof ResponseError)) return null;
  if (error.response.status === 403) return 'forbidden';
  if (error.response.status !== 404) return null;
  switch (detail?.code) {
    case NotFoundErrorDetailCodeEnum.SalesRepNotFound:
      return 'link';
    case NotFoundErrorDetailCodeEnum.ModuleNotFound:
      return 'module';
    default:
      return null;
  }
}

export function repRefusal(rep: SalesRepDto): AgentUnavailableReason | null {
  if (!rep.isMember || !rep.holdsRepRole) return 'link';
  if (!rep.active || rep.missingSince) return 'inactive';
  return null;
}

/**
 * Opens the app to a member holding the sales role in the company with an active rep link;
 * anyone else lands on the page saying why. Any other failure of `reps/me` is left to the
 * layout, which offers a retry.
 */
export const agentGuard: CanActivateFn = async () => {
  const router = inject(Router);
  const queryClient = inject(QueryClient);
  const sales = inject(SalesApi);
  const ready = waitForMeAndCompany();

  const refuse = (reason: AgentUnavailableReason) => {
    const queryParams: AgentUnavailableParams = { reason };
    return router.createUrlTree(agentRouteCommands.unavailable(), {
      queryParams,
    });
  };

  const { me, companyId } = await ready;
  if (!me.companies[companyId]?.modules.includes(SALES_MODULE_KEY)) {
    return refuse('module');
  }

  // The layout goes on with the rep kept on the device: this read would wait for the network
  if (!apiOnline()) return true;

  try {
    const rep = await queryClient.fetchQuery(
      myRepQueryOptions(sales, companyId),
    );
    const reason = repRefusal(rep);
    return reason ? refuse(reason) : true;
  } catch (error) {
    const reason = repErrorRefusal(error, await parseApiErrorDetail(error));
    return reason ? refuse(reason) : true;
  }
};
